import { useMemo } from 'react';

import { Combobox, Group, TextInput, useCombobox } from 'ui';
import { useDebouncedValue } from 'ui/Hooks';
import { IconSearch } from 'ui/Icons';

import { OnPlaceSelect, usePlacesAutoComplete } from '../hooks/usePlacesAutoComplete';

interface MapLocationSearchProps {
  onPlaceSelect: OnPlaceSelect;
  /**
   * The placeholder text for the text input
   */
  placeholder?: string;
  /**
   * Optional callback to format the input value when a place is selected
   * If not provided, defaults to using formatted_address
   */
  formatSelectedPlace?: (place: google.maps.places.Place) => string;
}
interface AutoCompleteAsyncProps {
  data?: google.maps.places.AutocompleteSuggestion[] | null;
  onChange: (value: string) => void | Promise<void>;
  value?: string | number | readonly string[] | undefined;
  onOptionSelect: (suggestion: google.maps.places.AutocompleteSuggestion) => void | Promise<void>;
  placeholder?: string;
}

const AutoComplete = ({
  data,
  onChange,
  value,
  onOptionSelect,
  placeholder,
}: AutoCompleteAsyncProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = useMemo(
    () =>
      (data || []).map((item, index) => (
        <Combobox.Option
          value={item?.placePrediction?.text?.text ?? ''}
          key={item?.placePrediction?.placeId ?? `suggestion-${index}`}
        >
          <Group wrap="nowrap">{item.placePrediction?.text?.text}</Group>
        </Combobox.Option>
      )),
    [data]
  );

  //Throttle the empty so that on first character search we don't show no results found
  const isEmpty = useMemo(() => Boolean(value && options.length == 0), [options?.length, value]);
  const [throttledIsEmpty] = useDebouncedValue(isEmpty, 400);

  return (
    <Combobox
      onOptionSubmit={(optionValue) => {
        void onChange(optionValue);
        const suggestion = data?.find((item) => item.placePrediction?.text?.text === optionValue);
        if (suggestion) {
          void onOptionSelect(suggestion);
        }
        combobox.closeDropdown();
      }}
      withinPortal={true}
      store={combobox}
    >
      <Combobox.Target>
        <TextInput
          placeholder={placeholder ?? 'Search city or zip code'}
          value={value}
          onChange={(event) => {
            void onChange(event.currentTarget.value);
            combobox.resetSelectedOption();
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          leftSection={<IconSearch />}
          onBlur={() => combobox.closeDropdown()}
          id="map-location-search"
        />
      </Combobox.Target>

      <Combobox.Dropdown hidden={!value || !options}>
        <Combobox.Options>
          {options}
          {throttledIsEmpty == true ? <Combobox.Empty>No results found</Combobox.Empty> : null}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

const MapLocationSearch = ({
  onPlaceSelect,
  placeholder,
  formatSelectedPlace,
}: MapLocationSearchProps) => {
  const { predictionResults, onInputChange, inputValue, handleSuggestionClick } =
    usePlacesAutoComplete({
      onPlaceSelect: (place) => {
        onPlaceSelect(place);
      },
      formatSelectedPlace,
    });
  return (
    <AutoComplete
      data={predictionResults}
      onChange={onInputChange}
      value={inputValue}
      onOptionSelect={handleSuggestionClick}
      placeholder={placeholder}
    />
  );
};

export { MapLocationSearch };
